import Utils from '../../../utils/utils';
import { dispatch, getSharedDataObject } from '../state-manager';
import { setSharedProperty, setStateProperty } from '../actions/action-creators';
import SuggestionsContext from '../suggestions-utils';
import { refreshOnPropertyChange } from '../../../refresh/refreshHelpers';
/**
 * helper functions for Redux State
 */
class StateUtils {
  /**
   * this function helps to resolve the property value from the shared state (Redux store).
   * @param key property whose data value is returned
   * @returns property value from the shared state (Redux store) if shared object present else return null.
   * @public
   * @static
   * @example <caption>Example for getSharedState </caption>
   * const callInfo = PCore.getStateUtils().getSharedState('sharedObject');
   * callInfo variable contains the value retrieved from shared state of redux store.
   *  @example <caption>Example for sharedObject </caption>
   * {
      number: '+(603) 345 456'
      status: 'Online'
   * }
   */
  static getSharedState(key: string) {
    const resolvedProp = key.startsWith('.') ? key.substring(1) : key;
    const shareddataObject = getSharedDataObject();
    return shareddataObject ? Utils.getIn(shareddataObject, resolvedProp) : null;
  }

  /**
   * this function helps to assign the value to property name (or key) in state (Redux store).
   * @param key property name whose data value need to set
   * @param value property value which needs to get assigned to the passed key in Redux Store.
   * @public
   * @static
   * @example <caption>Example for setSharedState </caption>
   * PCore.getStateUtils().setSharedState('sharedObject', {
      number: '+(603) 345 456'
      status: 'Online'
   * });
   * // here examples shows setting the `lorem ipsum` value in firstName property
   */
  static setSharedState(key: string, value: any) {
    if (!key) return;
    dispatch(
      setSharedProperty({
        key,
        value
      })
    );
  }

  /**
   * This API Updates the value of a property in the Store with the specified context and page reference.
   * @param context The context of the property that must be updated
   * @param key The ID of the property whose value must be updated
   * @param value The value that must be assigned to the property
   * @param options - The object containing the properties to enhance the functionality of this API
   * @param options.pageReference - The reference to the page that contains the property that must be updated
   * @param options.isArrayDeepMerge - The flag that decides if the values within an array must be deep merged or replaced in the Store
   * @public
   * @static
   * @example <caption>Example for updateState</caption>
   * PCore.getStateUtils().updateState(context, key, value, options);
   * PCore.getStateUtils().updateState("app/modal_1", "City", "Hyderabad", { pageReference: "caseInfo.content" });
   * PCore.getStateUtils().updateState("app/modal_1", "CountryList", [], { pageReference: "caseInfo.content", isArrayDeepMerge: false });
   */

  static updateState(
    context: string,
    key: string,
    value: any,
    options: {
      pageReference?: string;
      isArrayDeepMerge?: boolean;
      removePropertyFromChangedList?: boolean;
      skipDirtyValidation?: boolean;
    } = {}
  ) {
    const {
      isArrayDeepMerge = true,
      pageReference,
      removePropertyFromChangedList = false,
      skipDirtyValidation = false
    } = options;
    if (!context || !key) return;
    const propFullReference = pageReference ? `${pageReference}.${key}` : key;
    dispatch(
      setStateProperty({
        context,
        reference: propFullReference,
        value,
        isArrayDeepMerge,
        removePropertyFromChangedList,
        skipDirtyValidation
      })
    );
  }

  /**
   * Obtains an entry point to the SuggestionsContext object that contains APIs to perform actions on the Suggestions Context.
   * @public
   * @param context The name of the context based on which the Suggestions Context is created.
   * @returns - SuggestionsContext object {@link SuggestionsContext}
   * @example <caption>Example for getSuggestionsContext</caption>
   * In this example, the API returns the SuggestionsContext object for the app/primary_1/workarea_1 context.
   * PCore.getStateUtils().getSuggestionsContext('app/primary_1/workarea_1')
   */
  static getSuggestionsContext(context: string) {
    return new SuggestionsContext(context);
  }

  /**
   * This API refreshes the assignment present in the specified context.
   * @param context - The name context where the refresh has to be performed.
   * @param pageReference - The reference to the page that the assignment has to refreshed.
   */
  static triggerRefresh(context: string, pageReference: string) {
    refreshOnPropertyChange({ context, pageReference });
  }
}

export default StateUtils;
